class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close()
    );
    this.querySelector("#CartDrawer-Overlay").addEventListener(
      "click",
      this.close.bind(this)
    );
    this.setHeaderCartIconAccessibility();
    document.addEventListener("cart:update", (event) => this.refresh());
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
      this.hideLoader();
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");
        // console.log(containerToTrapFocusOn, this.classList, this.focusElement);
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add("overflow-hidden");
  }

  close() {
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open")
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  async refresh() {
    this.showLoader();
    const sections = this.getSectionsToRender().map((section) => section.id);

    this.setActiveElement(document.activeElement);

    const response = await fetch("/cart.js");

    const data = await response.json();

    if (data.items.length > 0) {
      this.classList.remove("is-empty");
    } else {
      this.classList.add("is-empty");
    }

    fetch(`?sections=${sections.join(",")}`)
      .then((response) => response.json())
      .then((response) => this.renderContents({ sections: response }))
      .catch(() => {
        this.hideLoader();
      });
  }

  renderContents(parsedState) {
    this.querySelector(".drawer__inner").classList.contains("is-empty") &&
      this.querySelector(".drawer__inner").classList.remove("is-empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    setTimeout(() => {
      this.querySelector("#CartDrawer-Overlay").addEventListener(
        "click",
        this.close.bind(this)
      );
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  showLoader() {
    document.querySelector(".body-loader").classList.add("active");
  }

  hideLoader() {
    document.querySelector(".body-loader").classList.remove("active");
  }

  async addToCart(body, headers = {}) {
    try {
      this.showLoader();
      const response = await fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...headers,
        },
        body: body,
      });

      const responseJson = await response.json();

      if (!response.ok) {
        throw new Error(
          responseJson.description || "Error adding item to cart"
        );
      }

      this.refresh();

      return responseJson;
    } catch (error) {
      console.error("Error:", error);
      this.hideLoader();
      throw error;
    } finally {
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);

class CartUpsell extends HTMLElement {
  constructor() {
    super();

    this.cart = this.closest("cart-drawer-items");
    this.upsellForms = this.querySelectorAll(".upsell-item__actions-form");
    this.moreButtons = this.querySelectorAll(".upsell-prices__more");
    this.cartUpsellContainer = document.querySelector("#cart-drawer-upsell");
    this.modal = document.querySelector(".cart-drawer__body-modal");
  }

  connectedCallback() {
    this.upsellForms.forEach((form) =>
      form.addEventListener("submit", (event) => this.addToCart(event))
    );
    this.moreButtons.forEach((button) =>
      button.addEventListener("click", () => this.showModal(button))
    );
  }

  showModal(button) {
    const productInfo = button.closest(".upsell-item__info");
    const template = productInfo.querySelector("template");
    new ModalUpsellProduct(template, this.modal);
  }

  async addToCart(event) {
    event.preventDefault();

    const cartDrawer = this.closest("cart-drawer");
    try {
      cartDrawer.showLoader();
      await this.addToCartRequest(event.target);
      this.updateCart();
      if (
        this.modal &&
        this.modal.classList.contains("cart-drawer__body-modal--active")
      ) {
        this.modal.classList.remove("cart-drawer__body-modal--active");
        this.modal.addEventListener("transitionend", this.clearModal());
        this.modal.removeEventListener("transitionend", this.clearModal());
      }
    } catch (error) {
      console.error("Error:", error);
    }
  }

  addToCartRequest(form) {
    const config = {
      method: "POST",
      body: new FormData(form),
    };

    return fetch(`${window.routes.cart_add_url}.js`, config);
  }

  clearModal() {
    this.modal.innerHTML = "";
  }

  updateCart() {
    document.dispatchEvent(new CustomEvent("cart:update", { bubbles: true }));
  }

  updateUpsell(markup) {
    if (markup) {
      this.cartUpsellContainer.replaceWith(markup);
    }
  }

  async fetchUpsellMarkup() {
    const SECTION_ID = "api-cart-drawer-upsell";
    const productUrl = `${window.location.origin}?section_id=${SECTION_ID}`;

    try {
      const response = await fetch(productUrl);
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, "text/html");
      return html.querySelector(".shopify-section #cart-drawer-upsell");
    } catch (error) {
      console.error("Failed to fetch upsell markup:", error);
    }
  }

  async renderCartUpsell() {
    const markup = await this.fetchUpsellMarkup();
    this.updateUpsell(markup);
  }
}

customElements.define("cart-upsell", CartUpsell);
document.addEventListener('click', function (event) {
  const cartDrawer = document.querySelector('#CartDrawer');
  const cartDrawerToggle = document.querySelector('.drawer__close');
  const drawer = document.querySelector('.drawer');

  if (!cartDrawer || !cartDrawerToggle || !drawer) return;

  // Only run if drawer is active and click is outside the CartDrawer
  if (drawer.classList.contains('active') && !cartDrawer.contains(event.target)) {
    cartDrawerToggle.click();
  }
});
